import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox,
  Loader2,
  AtSign,
  Share2,
  MessageSquare,
  Clock,
  Check,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getInbox } from "../../../../api/home";

/* ── Timestamp formatter ── */
function timeAgo(ts) {
  if (!ts) return "";
  const secs = Math.floor(Date.now() / 1000) - ts;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d`;
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/* ── Type badge config ── */
const TYPE_CONFIG = {
  mention: { label: "Mention", icon: AtSign, color: "#8b5cf6" },
  share_invite: { label: "Share", icon: Share2, color: "#0ea5e9" },
  dm: { label: "DM", icon: MessageSquare, color: "#10b981" },
};

/* ── Inbox item card ── */
const InboxItem = ({ item, dark, t, onClick }) => {
  const msg = item.message || {};
  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.dm;
  const Icon = cfg.icon;

  const senderName = msg.sender_name || "Unknown";
  const content = msg.content || "";
  const preview =
    content.length > 120 ? content.slice(0, 119) + "..." : content;
  const convName = item.conversation_name || "";
  const ts = timeAgo(item.timestamp);

  // For share invites, extract item name
  const shareMeta = msg.share_meta || {};
  const sharePreview = shareMeta.preview || {};
  const shareItemName =
    sharePreview.item_name || sharePreview.company_name || "";
  const shareOwnerName = shareMeta.owner_name || senderName;
  const shareContentType = shareMeta.content_type || "";

  const formatContentType = (ct) => {
    if (!ct) return "";
    return ct.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const borderColor = dark ? "rgba(14,165,233,0.08)" : "rgba(203,213,225,0.3)";

  return (
    <div
      onClick={() => onClick?.(item)}
      className="flex gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer transition-colors hover:bg-white/[0.03]"
      style={{ border: `1px solid ${borderColor}` }}
    >
      {/* Type icon */}
      <div
        className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: cfg.color + "15" }}
      >
        <Icon
          className="h-3.5 w-3.5"
          strokeWidth={2}
          style={{ color: cfg.color }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {item.type === "share_invite" ? (
          <>
            <div className="flex items-center gap-1.5">
              <span
                className="text-[11px] font-medium truncate"
                style={{ color: t.textPrimary }}
              >
                {shareItemName || "Shared item"}
              </span>
              {shareContentType && (
                <span
                  className="text-[8px] px-1 py-0.5 rounded-full shrink-0"
                  style={{ background: cfg.color + "15", color: cfg.color }}
                >
                  {formatContentType(shareContentType)}
                </span>
              )}
            </div>
            <p
              className="text-[10px] mt-0.5 truncate"
              style={{ color: t.textMuted }}
            >
              From {shareOwnerName}
              {convName ? ` in ${convName}` : ""}
            </p>
            {content && (
              <p
                className="text-[10px] mt-0.5 italic truncate"
                style={{ color: t.textMuted }}
              >
                "{preview}"
              </p>
            )}
          </>
        ) : item.type === "mention" ? (
          <>
            <div className="flex items-center gap-1.5">
              <span
                className="text-[11px] font-medium truncate"
                style={{ color: t.textPrimary }}
              >
                {senderName}
              </span>
              <span
                className="text-[9px] shrink-0"
                style={{ color: t.textMuted }}
              >
                mentioned you
              </span>
              {convName && (
                <span
                  className="text-[9px] truncate shrink-0"
                  style={{ color: t.textMuted }}
                >
                  in {convName}
                </span>
              )}
            </div>
            {content && (
              <p
                className="text-[10px] mt-0.5 truncate"
                style={{ color: t.textSecondary }}
              >
                {preview}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <span
                className="text-[11px] font-medium truncate"
                style={{ color: t.textPrimary }}
              >
                {senderName}
              </span>
            </div>
            {content && (
              <p
                className="text-[10px] mt-0.5 truncate"
                style={{ color: t.textSecondary }}
              >
                {preview}
              </p>
            )}
          </>
        )}
      </div>

      {/* Timestamp */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span
          className="text-[9px] tabular-nums whitespace-nowrap"
          style={{ color: t.textMuted }}
        >
          {ts}
        </span>
        {/* Unread dot */}
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: cfg.color }}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   InboxPanel
   ═══════════════════════════════════════════════════════════════ */
const InboxPanel = ({ dark, t }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getInbox()
      .then((res) => {
        if (res?.data?.status === 200) {
          setItems(res.data.items || []);
        } else {
          setError(res?.data?.message || "Failed to load inbox");
        }
      })
      .catch((e) => setError(e?.message || "Failed"))
      .finally(() => setLoading(false));
  }, []);

  const navigate = useNavigate();

  const handleItemClick = useCallback(
    (item) => {
      const teamId = item.team_id;
      if (!teamId) return;

      navigate("/chat");

      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );

      requestAnimationFrame(() => {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("sidebar:chat-thread-selected", {
              detail: {
                threadId: null,
                thread_type: "team",
                team_id: teamId,
              },
            }),
          );
        }, 50);
      });
    },
    [navigate],
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 shrink-0"
        style={{ borderBottom: `1px solid ${t.cardBorder}` }}
      >
        <div className="flex items-center gap-2">
          <Inbox
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: t.textSecondary }}
            strokeWidth={2}
          />
          <span
            className="text-[12px] font-medium"
            style={{ color: t.textPrimary }}
          >
            Inbox
          </span>
          {items.length > 0 && !loading && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full tabular-nums"
              style={{
                background: "rgba(14,165,233,0.15)",
                color: "rgba(14,165,233,0.9)",
              }}
            >
              {items.length}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto px-3 py-2"
        style={{ scrollbarWidth: "thin" }}
      >
        {loading ? (
          <div
            className="h-full flex items-center justify-center gap-2"
            style={{ color: t.textMuted }}
          >
            <Loader2
              size={14}
              className="animate-spin"
              style={{ color: t.activeText }}
            />
            <span className="text-[11px]">Loading inbox...</span>
          </div>
        ) : error ? (
          <div
            className="h-full flex items-center justify-center text-[11px]"
            style={{ color: t.dangerText }}
          >
            {error}
          </div>
        ) : items.length === 0 ? (
          <div
            className="h-full flex flex-col items-center justify-center gap-2"
            style={{ color: t.textMuted }}
          >
            <Check size={20} style={{ opacity: 0.3 }} />
            <span className="text-[11px]">You're all caught up</span>
          </div>
        ) : (
          <div className="space-y-1.5">
            {items.map((item, i) => (
              <motion.div
                key={item.message?.message_id || i}
                initial={{ opacity: 0, y: 8, filter: "blur(3px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.3,
                  delay: i * 0.05,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
              >
                <InboxItem
                  item={item}
                  dark={dark}
                  t={t}
                  onClick={handleItemClick}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxPanel;
